import {
  Box,
  Button,
  Typography,
  useTheme,
  useMediaQuery,
  Chip,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { Close } from "@mui/icons-material";
import { AppDispatch, RootState } from "../../stores";
import { getCustomerUserAssignAndPublicPlan } from "../../features/plan-management-module/planAction";
import {
  getBillingDetailsByUserId,
  getSubscriptionByUserId,
  getSubscriptionDetailsAction,
  getScheduledPlanAction,
  cancelSubscriptionAction,
  getBillingHistoryAction,
} from "../../features/subscription-module/subscriptionAction";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import PaymentForm from "./PaymentForm";
import { getAuthenticatedUser } from "../../features/auth-module/authAction";

// Load Stripe with public key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);

const Container = styled(Box)(({ theme }) => ({
  backgroundColor: "#f8f8f8",
  padding: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  borderRadius: 5,
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(3),
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const PlansContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: theme.spacing(2),
  margin: theme.spacing(2, "auto"),
  [theme.breakpoints.down("lg")]: {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
    gap: theme.spacing(1.5),
  },
}));

const PlanCard = styled(Box)(({ theme }) => ({
  backgroundColor: "#fff",
  borderRadius: 8,
  width: "100%",
  maxWidth: "auto",
  minWidth: 250,
  minHeight: 550,
  padding: theme.spacing(1),
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  textAlign: "center",
  transition: "transform 0.24s, background 0.24s",
  position: "relative",
  [theme.breakpoints.down("md")]: {
    maxWidth: "100%",
    marginBottom: theme.spacing(2),
  },
  "&:hover": {
    transform: "scale(1.03)",
    background: "linear-gradient(to bottom, #758d8e, #4b5b5d)",
    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
    "& .description-text": {
      color: "#f2efe9",
    },
    "& .feature-item": {
      color: "#f2efe9",
    },
    "& .buy-button": {
      backgroundColor: "#f2efe9",
      color: "#455354",
    },
  },
}));

const PlanTitleCard = styled(Box)(({ theme }) => ({
  backgroundColor: "#f2efe9",
  borderRadius: 8,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  textAlign: "left",
  transition: "transform 0.24s",
  position: "relative",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
  },
}));

const PlanPrice = styled(Typography)(({ theme }) => ({
  fontSize: 28,
  fontWeight: 600,
  color: "#455354",
  marginBottom: theme.spacing(1.5),
  letterSpacing: "-0.02em",
}));

const FeaturesList = styled(Box)(({ theme }) => ({
  textAlign: "left",
  marginTop: theme.spacing(2),
  padding: theme.spacing(1.2),
}));

const FeatureItem = styled(Typography)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  fontSize: 12,
  color: "#3f3f3f",
  marginBottom: theme.spacing(1),
  "&::before": {
    content: '"✓"',
    color: "#4caf50",
    fontWeight: 600,
    fontSize: 17,
    marginRight: theme.spacing(1.1),
    marginTop: 1,
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: 13,
    "&::before": {
      fontSize: 15,
      marginRight: theme.spacing(0.8),
    },
  },
}));

const CustomChip = styled(Chip)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(3.5),
  right: theme.spacing(0.5),
  background: "linear-gradient(to bottom, #8ec5aa, #5c8e78)",
  color: "#fff",
  fontWeight: 600,
  borderRadius: 4,
  height: 28,
  zIndex: 1,
  "& .MuiChip-label": {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  "& .MuiChip-icon": {
    color: "#fff",
    marginRight: theme.spacing(0.5),
  },
}));

const CurrentPlanChip = styled(Chip)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(3.5),
  right: theme.spacing(0.5),
  background: "linear-gradient(to bottom, #4b5b5d, #354142)",
  color: "#fff",
  fontWeight: 600,
  borderRadius: 4,
  height: 28,
  zIndex: 1,
  "& .MuiChip-label": {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
}));

const PlanLabelChip = styled(Chip)(({ theme }) => ({
  backgroundColor: "#fff",
  color: "#455354",
  fontWeight: 600,
  borderRadius: 4,
  height: 26,
  fontSize: 14,
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    height: 24,
    fontSize: 12,
  },
}));

const FeatureLabelChip = styled(Chip)(({ theme }) => ({
  backgroundColor: "#f2efe9",
  color: "#455354",
  fontWeight: 600,
  borderRadius: 4,
  height: 26,
  fontSize: 14,
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    height: 24,
    fontSize: 12,
  },
}));

const MessageCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  maxWidth: 400,
  width: "100%",
  textAlign: "center",
  margin: theme.spacing(2, "auto"),
  backgroundColor: "#fff",
  borderRadius: 8,
  boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    maxWidth: "90%",
  },
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    backgroundColor: "#fff",
    borderRadius: 12,
    maxWidth: 600,
    width: "100%",
    boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
    [theme.breakpoints.down("sm")]: {
      margin: theme.spacing(1),
      maxWidth: "95%",
    },
  },
}));

const DialogHeader = styled(Box)(({ theme }) => ({
  background: "linear-gradient(to bottom, #758d8e, #4b5b5d)",
  padding: theme.spacing(2),
  color: "#f2efe9",
  borderTopLeftRadius: 12,
  borderTopRightRadius: 12,
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
  },
}));

const DialogBody = styled(DialogContent)(({ theme }) => ({
  backgroundColor: "#fff",
  padding: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const DialogFooter = styled(DialogActions)(({ theme }) => ({
  backgroundColor: "#f2efe9",
  padding: theme.spacing(2),
  justifyContent: "center",
  borderBottomLeftRadius: 12,
  borderBottomRightRadius: 12,
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
    flexDirection: "column",
    gap: theme.spacing(1),
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  backgroundColor: "#4b5b5d",
  color: "#fff",
  borderRadius: 8,
  padding: theme.spacing(1, 3),
  fontSize: 13,
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  "&:hover": {
    backgroundColor: "#354142",
  },
  "&.Mui-disabled": {
    backgroundColor: "#bdbdbd",
    color: "#fff",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: 14,
    padding: theme.spacing(1, 2),
  },
}));

const CancelButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  backgroundColor: "transparent",
  color: "#455354",
  borderRadius: 8,
  padding: theme.spacing(1, 3),
  fontSize: 13,
  "&:hover": {
    backgroundColor: "rgba(69, 83, 84, 0.1)",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: 14,
    padding: theme.spacing(1, 2),
  },
}));

// Loader Container
const LoaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "400px",
  width: "100%",
  height: "100%",
  marginTop: theme.spacing(4),
}));

const LoaderText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  fontSize: "0.9rem",
  color: "#4b5b5d",
  fontWeight: 500,
}));

interface Plan {
  id: string;
  planName: string;
  price: number;
  duration: string;
  description: string;
  features: string[];
  fleetEstimatorReportLimit: number;
  siteEstimatorReportLimit: number;
  isPopular?: boolean;
  isFleetEstimator?: boolean;
  isSiteEstimator?: boolean;
  mode: "public" | "private";
  userId: string | null;
}

export const dispatchUserSubscriptionActions = async (
  dispatch: AppDispatch
) => {
  await Promise.all([
    dispatch(getSubscriptionByUserId() as any),
    dispatch(getSubscriptionDetailsAction() as any),
    dispatch(getBillingDetailsByUserId() as any),
    dispatch(getBillingHistoryAction() as any),
    dispatch(getScheduledPlanAction() as any),
    dispatch(getAuthenticatedUser() as any),
  ]);
};

const UpgradePlan = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const {
    customerPlans: plans,
    error: planError,
    loading: plansLoading,
  } = useSelector((state: RootState) => state.planManagement);
  const {
    subscription,
    error: subscriptionError,
    loading: subscriptionLoading,
    scheduledPlan,
    scheduledPlanLoading,
    scheduledPlanError,
    updateSubscriptionLoading,
    cancelSubscriptionLoading,
    cancelSubscriptionError,
  } = useSelector((state: RootState) => state.subscription);
  const [selectedDuration, setSelectedDuration] = useState<string>("monthly");
  const [openDialog, setOpenDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [currentPlanId, setCurrentPlanId] = useState<string | null>(null);
  const [upcomingPlanId, setUpcomingPlanId] = useState<string | null>(null);

  useEffect(() => {
    dispatch(
      getCustomerUserAssignAndPublicPlan({ page: 1, limit: 100 }) as any
    );
    dispatchUserSubscriptionActions(dispatch);
  }, [dispatch]);

  useEffect(() => {
    if (subscription && plans.length > 0) {
      const matchedPlan = plans.find((plan) => plan.id === subscription.planId);
      if (matchedPlan) {
        setCurrentPlanId(matchedPlan.id);
      } else {
        setCurrentPlanId(null);
      }
    } else {
      setCurrentPlanId(null);
    }
  }, [subscription, plans]);

  useEffect(() => {
    if (scheduledPlan) {
      setUpcomingPlanId(scheduledPlan.scheduledPlanDetails.planId);
    } else {
      setUpcomingPlanId(null);
    }
  }, [scheduledPlan]);

  useEffect(() => {
    if (planError) {
      toast.error(planError);
    }
    if (
      scheduledPlanError &&
      !scheduledPlanError.includes("No scheduled subscription found")
    ) {
      toast.error(scheduledPlanError);
    }
    if (cancelSubscriptionError) {
      toast.error(cancelSubscriptionError);
    }
  }, [planError, scheduledPlanError, cancelSubscriptionError]);

  const handleDurationChange = (duration: string) => {
    setSelectedDuration(duration);
  };

  const handlePlanAction = (plan: Plan) => {
    if (plan.planName.toLowerCase() === "enterprise") {
      navigate("/custom-lead");
    } else if (plan.id === currentPlanId) {
      if (upcomingPlanId) {
        toast.error("You already have a scheduled plan");
      } else {
        setOpenCancelDialog(true);
      }
    } else if (plan.id !== upcomingPlanId) {
      setSelectedPlan(plan);
      setOpenDialog(true);
    }
  };

  const handleCancelConfirm = async () => {
    setOpenCancelDialog(false);
    try {
      const resultAction = await dispatch(cancelSubscriptionAction() as any);
      if (cancelSubscriptionAction.fulfilled.match(resultAction)) {
        await dispatchUserSubscriptionActions(dispatch);
        const cancelAt = resultAction.payload.data?.cancelAt;
        if (cancelAt) {
          const formattedDate = new Date(cancelAt).toLocaleDateString();
          toast.success(`Subscription will cancel on ${formattedDate}`);
        } else {
          toast.success("Subscription scheduled to cancel successfully");
        }
      } else {
        toast.error(resultAction.payload || "Failed to cancel subscription");
      }
    } catch (error) {
      toast.error("An error occurred while cancelling subscription");
    }
  };

  const handleCloseDialog = async () => {
    setOpenDialog(false);
    setSelectedPlan(null);
    await dispatchUserSubscriptionActions(dispatch);
  };

  const filteredPlans = selectedDuration
    ? plans.filter(
        (plan) =>
          plan.duration === selectedDuration || plan.duration === "custom"
      )
    : plans;

  const isNoSubscription =
    subscriptionError && subscriptionError.includes("Subscription not found");

  // Format endDate if available
  const formattedEndDate = subscription?.endDate
    ? new Date(subscription.endDate).toLocaleDateString()
    : null;

  return (
    <Container>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: isMobile ? "center" : "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          width: "100%",
          mb: 2,
          gap: isMobile ? theme.spacing(1) : 0,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "#4b5b5d",
            fontSize: isMobile ? 20 : 25,
            textAlign: "center",
          }}
        >
          {isNoSubscription ? "Choose a Plan" : "Upgrade Plans"}
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: theme.spacing(1),
          }}
        >
          <Button
            variant="contained"
            onClick={() => handleDurationChange("monthly")}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: isMobile ? 14 : 16,
              padding: theme.spacing(1, 2),
              backgroundColor:
                selectedDuration === "monthly" ? "#4b5b5d" : "#f5f5f5",
              color: selectedDuration === "monthly" ? "#fff" : "#4b5b5d",
              "&:hover": {
                backgroundColor:
                  selectedDuration === "monthly" ? "#354142" : "#e0e0e0",
              },
              [theme.breakpoints.down("sm")]: {
                padding: theme.spacing(0.75, 1.5),
                width: "100%",
              },
            }}
          >
            Monthly
          </Button>
          <Button
            variant="contained"
            onClick={() => handleDurationChange("yearly")}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: isMobile ? 14 : 16,
              padding: theme.spacing(1, 2),
              backgroundColor:
                selectedDuration === "yearly" ? "#4b5b5d" : "#f5f5f5",
              color: selectedDuration === "yearly" ? "#fff" : "#4b5b5d",
              "&:hover": {
                backgroundColor:
                  selectedDuration === "yearly" ? "#354142" : "#e0e0e0",
              },
              [theme.breakpoints.down("sm")]: {
                padding: theme.spacing(0.75, 1.5),
                width: "100%",
              },
            }}
          >
            Yearly
          </Button>
        </Box>
      </Box>

      {plansLoading || subscriptionLoading || scheduledPlanLoading ? (
        <LoaderContainer>
          <CircularProgress
            size={40}
            thickness={4}
            sx={{
              color: "#4b5b5d",
            }}
          />
          <LoaderText>Loading plans...</LoaderText>
        </LoaderContainer>
      ) : planError ? (
        <MessageCard>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              color: "#4b5b5d",
              fontSize: isMobile ? 14 : 16,
            }}
          >
            {planError || "Failed to load plans. Please try again."}
          </Typography>
        </MessageCard>
      ) : filteredPlans.length === 0 ? (
        <MessageCard>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              color: "#4b5b5d",
              fontSize: isMobile ? 14 : 16,
            }}
          >
            No plans available for the selected duration.
          </Typography>
        </MessageCard>
      ) : (
        <PlansContainer>
          {filteredPlans.map((plan) => {
            const isCurrentPlan = plan.id === currentPlanId;
            const isUpcomingPlan = plan.id === upcomingPlanId;
            const isCancelScheduled = isCurrentPlan && !!subscription?.endDate;
            return (
              <PlanCard key={plan.id}>
                {plan.isPopular && !isCurrentPlan && (
                  <CustomChip
                    label="Popular"
                    icon={
                      <span style={{ fontSize: isMobile ? 12 : 14 }}>🔥</span>
                    }
                  />
                )}
                {isCurrentPlan && <CurrentPlanChip label="Current Plan" />}
                <PlanTitleCard>
                  <PlanLabelChip label={plan.planName} />
                  <PlanPrice
                    sx={{
                      cursor:
                        plan.planName.toLowerCase() === "enterprise"
                          ? "pointer"
                          : "default",
                    }}
                    onClick={
                      plan.planName.toLowerCase() === "enterprise"
                        ? () => handlePlanAction(plan)
                        : undefined
                    }
                  >
                    {plan.planName.toLowerCase() === "enterprise"
                      ? "Custom Pricing"
                      : `$${plan.price}/${
                          plan.duration === "monthly" ? "month" : "year"
                        }`}
                  </PlanPrice>
                </PlanTitleCard>
                <Typography
                  color="#666"
                  sx={{ mb: 2, textAlign: "left", mt: 2, fontSize: "0.75rem" }}
                  className="description-text"
                >
                  {plan.description}
                </Typography>
                <Button
                  variant="contained"
                  className="buy-button"
                  onClick={() => handlePlanAction(plan)}
                  disabled={
                    isUpcomingPlan ||
                    cancelSubscriptionLoading ||
                    isCancelScheduled
                  }
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    backgroundColor:
                      isCurrentPlan || isUpcomingPlan || isCancelScheduled
                        ? "#bdbdbd"
                        : "#355142",
                    color: "#fff",
                    width: "100%",
                    borderRadius: 2,
                    padding: theme.spacing(1),
                    fontSize: 12,
                    marginBottom: theme.spacing(1.5),
                    boxShadow: "0 1px 8px rgba(0,0,0,0.08)",
                    "&:hover": {
                      backgroundColor:
                        isCurrentPlan || isUpcomingPlan || isCancelScheduled
                          ? "#bdbdbd"
                          : "#283b2e",
                    },
                    [theme.breakpoints.down("sm")]: {
                      fontSize: 14,
                      padding: theme.spacing(1),
                    },
                  }}
                >
                  {cancelSubscriptionLoading && isCurrentPlan ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress
                        size={isMobile ? 16 : 20}
                        sx={{ color: "#4b5b5d" }}
                      />
                      <Typography variant="button">Cancelling...</Typography>
                    </Box>
                  ) : plan.planName.toLowerCase() === "enterprise" ? (
                    "Contact Us"
                  ) : isCurrentPlan && !isCancelScheduled ? (
                    "Cancel Subscription"
                  ) : isCurrentPlan && isCancelScheduled ? (
                    "Cancellation Scheduled"
                  ) : isUpcomingPlan ? (
                    "Upcoming Plan"
                  ) : (
                    "Change Plan"
                  )}
                </Button>
                {isCurrentPlan && isCancelScheduled && (
                  <Typography
                    sx={{
                      color: "#d32f2f",
                      fontSize: isMobile ? 12 : 14,
                      textAlign: "center",
                      marginBottom: theme.spacing(1.5),
                    }}
                  >
                    Cancels on {formattedEndDate}
                  </Typography>
                )}
                <Divider sx={{ mb: 2 }} />
                <FeaturesList>
                  <FeatureLabelChip label="Feature List" />
                  {plan.features.map((feature, idx) => (
                    <FeatureItem key={idx} className="feature-item">
                      {feature}
                    </FeatureItem>
                  ))}
                </FeaturesList>
              </PlanCard>
            );
          })}
        </PlansContainer>
      )}

      {/* Upgrade/Subscribe Dialog */}
      <StyledDialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogHeader>
          <DialogTitle
            sx={{ color: "#f2efe9", p: 0, m: 0, fontSize: isMobile ? 18 : 20 }}
          >
            {isNoSubscription
              ? "Start Your Subscription"
              : "Update Your Subscription"}
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: "#f2efe9",
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogHeader>
        <DialogBody>
          {selectedPlan && (
            <Elements stripe={stripePromise}>
              <PaymentForm
                selectedPlan={selectedPlan}
                onClose={handleCloseDialog}
              />
            </Elements>
          )}
        </DialogBody>
        <DialogFooter>
          <CancelButton onClick={handleCloseDialog}>Cancel</CancelButton>
          <StyledButton
            type="submit"
            form="payment-form"
            variant="contained"
            disabled={updateSubscriptionLoading}
          >
            {updateSubscriptionLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress
                  size={isMobile ? 20 : 24}
                  sx={{ color: "#4b5b5d" }}
                />
                <Typography variant="button">Processing...</Typography>
              </Box>
            ) : isNoSubscription ? (
              "Start Subscription"
            ) : (
              "Update Subscription"
            )}
          </StyledButton>
        </DialogFooter>
      </StyledDialog>

      {/* Cancel Subscription Dialog */}
      <StyledDialog
        open={openCancelDialog}
        onClose={() => setOpenCancelDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogHeader>
          <DialogTitle
            sx={{ color: "#f2efe9", p: 0, m: 0, fontSize: isMobile ? 18 : 20 }}
          >
            Confirm Cancellation
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={() => setOpenCancelDialog(false)}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: "#f2efe9",
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogHeader>
        <DialogBody>
          <Typography variant="body1">
            Are you sure you want to cancel your subscription? It will remain
            active until the end of the current billing period.
          </Typography>
        </DialogBody>
        <DialogFooter>
          <CancelButton onClick={() => setOpenCancelDialog(false)}>
            No
          </CancelButton>
          <StyledButton
            onClick={handleCancelConfirm}
            variant="contained"
            disabled={cancelSubscriptionLoading}
          >
            {cancelSubscriptionLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress
                  size={isMobile ? 20 : 24}
                  sx={{ color: "#4b5b5d" }}
                />
                <Typography variant="button">Processing...</Typography>
              </Box>
            ) : (
              "Yes, Cancel"
            )}
          </StyledButton>
        </DialogFooter>
      </StyledDialog>
    </Container>
  );
};

export default UpgradePlan;
